import React from 'react';
import clsx from 'clsx';
import { Input } from '@/components/form';
import { FieldValues, useForm } from 'react-hook-form';
import { ModalReviewSignFields } from '../modal-review-sign/ModalReviewSignFields';
import {
	DocumentVersionHistory,
	HttpResponse,
	RequiredField,
	SignDocumentDetails,
} from '@/interfaces';
import { Button } from '@/components/common';
import Icons from '@/const/icons';
import { ConfirmDeclineCard } from './ConfirmDeclineCard';
import { useEditDocument } from '@/hooks/useEditDocument';
import { actionRequiredService } from '@/api';
import { useNotification } from '@/hooks';
import { HeaderNote } from '../HeaderNote';
import { useDebouncedCallback } from 'use-debounce';
import { getCurrentFormatDate } from '@/utils';
import { format } from 'date-fns';

interface Props {
	actionDetails: HttpResponse<SignDocumentDetails> | undefined;
	actionRequiredId: number | undefined;
	applicationId: number | undefined;
	isSignRequired: boolean;
	fields: RequiredField[];
	hasNextDocument: boolean;
	isCompleted: boolean;
	onContinue: (status: boolean) => void;
}

export const FormGeneralDocument: React.FC<Props> = ({
	actionRequiredId,
	applicationId,
	isSignRequired,
	actionDetails,
	hasNextDocument,
	isCompleted,
	onContinue,
	fields,
}) => {
	const { toastError } = useNotification();
	const [isConfirmingDecline, setIsConfirmingDecline] = React.useState(false);
	const [isDeclining, setIsDeclining] = React.useState(false);
	const { getTrackingData, setPageInfoToPdf, getFieldQuestions } =
		useEditDocument();
	const rules = {
		name: {
			required: { value: true, message: 'This field is required' },
			maxLength: {
				value: 120,
				message: 'Your name has to have less than 120 characters',
			},
		},
		signature: {
			required: { value: true, message: 'This field is required' },
			maxLength: {
				value: 120,
				message: 'Your signature has to have less than 120 characters',
			},
			validate: (value: string) =>
				!!value?.trim() || 'Whitespace only is not allowed',
		},
	};

	const {
		register,
		setValue,
		watch,
		getValues,
		handleSubmit,
		formState: { isValid, errors, isSubmitting },
	} = useForm({ mode: 'onChange', shouldUnregister: true });

	const signature = watch('signature');
	const isOnlyToAccept = fields.length === 0 && !isSignRequired;
	const setTimeStamp = useDebouncedCallback(() => {
		const formattedDate = getCurrentFormatDate();
		setValue('date', formattedDate);
	}, 1000);

	React.useEffect(() => {
		if (actionDetails?.data.signature && actionDetails.data.signed_at) {
			const data = actionDetails.data;
			setValue('signature', data.signature);
			setValue(
				'date',
				format(new Date(data.signed_at as string), 'MM/dd/yyyy')
			);
			actionDetails.data.required_fields.forEach((field) => {
				setValue(`field-${field.question_id}`, field.response);
			});
		}
	}, [actionDetails]);

	React.useEffect(() => {
		if (!signature) return;
		setTimeStamp();
	}, [signature]);

	const onSubmit = async (data: FieldValues, isApprove: boolean) => {
		if (!applicationId || !actionRequiredId) return;
		try {
			const trackingData = await getTrackingData();
			const { answers, plaintiffInfo } = getFieldQuestions(data, fields);
			const currentVersion = (
				actionDetails?.data.version_history as
					| DocumentVersionHistory[]
					| undefined
			)?.find((version) => version.is_current);
			const hasExistingSignaturePage =
				!!currentVersion?.signature_data &&
				currentVersion.file_path === actionDetails?.data.url_doc;
			const signedPdfBlob = await setPageInfoToPdf(
				data,
				plaintiffInfo,
				isApprove,
				actionDetails?.data,
				hasExistingSignaturePage
			);
			// this function exists inside the hook
			// if (signedPdfBlob) {
			// 	await downloadSignedPdf(signedPdfBlob, 'documento-firmado.pdf');
			// }

			const params: any = {
				application_id: String(applicationId),
				action_id: String(actionRequiredId),
				signature: isApprove && data.signature ? data.signature.trim() : null,
				name: isApprove && data.name ? data.name.trim() : null,
				status: String(isApprove),
				...trackingData,
			};

			if (signedPdfBlob) {
				params.doc = signedPdfBlob;
			}

			if (answers && isApprove) {
				params.responses = JSON.stringify(answers);
			}

			if (!isCompleted) {
				await actionRequiredService.signDocument(params);
			}
			onContinue(isApprove);
		} catch (error) {
			toastError(
				error instanceof Error
					? error.message
					: 'An error has occurred. Please try again.'
			);
		}
	};

	const handleDecline = async () => {
		if (isDeclining || isSubmitting) return;
		setIsDeclining(true);
		try {
			await onSubmit(getValues(), false);
		} finally {
			setIsDeclining(false);
		}
	};

	return (
		<>
			<HeaderNote showNote={false} />
			<form
				className={clsx('flex flex-col flex-1 min-h-0 relative')}
				onSubmit={(event) => event.preventDefault()}
			>
				<div
					className={clsx(
						'space-y-4 flex-1 min-h-0 overflow-y-auto scroll-custom',
						{
							'!h-auto': isOnlyToAccept,
						}
					)}
				>
					{isSignRequired && (
						<>
							<Input
								title="Signature"
								name="signature"
								register={register}
								rules={rules.signature}
								error={errors.signature}
								classNameInput="!font-praise !text-2xl disabled:!bg-[#F4F4F4A3] !h-12"
								disabled={isCompleted}
								maxLength={20}
							/>
							<Input
								title="Date"
								name="date"
								register={register}
								disabled
								className="w-full"
								classNameInput="disabled:!bg-[#F4F4F4A3]  !h-12"
								placeholder="MM/DD/YYY"
							/>
						</>
					)}
					<ModalReviewSignFields
						fields={fields}
						register={register}
						isCompleted={isCompleted}
						setValue={setValue}
						watch={watch}
						errors={errors}
					/>
				</div>
				<div
					className={clsx(
						'relative gap-2 md:justify-between mt-6 border-t pt-3',
						{
							'!border-0 md:!mt-0 !pt-0': isOnlyToAccept,
						}
					)}
				>
					<div className="flex items-center md:justify-end gap-2 lg:gap-4 w-full">
						<Button
							disabled={isSubmitting || isDeclining || isCompleted}
							decoration="danger-outline"
							onClick={() => setIsConfirmingDecline(true)}
							className={clsx('!w-auto lg:!w-fit text-[16px] leading-[120%]')}
						>
							Decline
						</Button>

						<Button
							iconRight={hasNextDocument}
							icon={Icons.arrowRight}
							disabled={isSubmitting || !isValid}
							decoration="primary"
							className="w-[106px] md:w-fit !h-12 !p-0 lg:!px-6 lg:!py-[14px]"
							classNameButton="!w-4 hidden lg:block"
							onClick={handleSubmit((data) => onSubmit(data, true))}
							label={hasNextDocument ? 'Agree & Continue' : 'Complete Signing'}
						/>
					</div>
					{isConfirmingDecline && (
						<div className="absolute right-0 bottom-0">
							<ConfirmDeclineCard
								isSubmitting={isSubmitting || isDeclining}
								onCancel={() => setIsConfirmingDecline(false)}
								onConfirm={() => {
									void handleDecline();
								}}
							/>
						</div>
					)}
				</div>
			</form>
		</>
	);
};
